import { getFunctions, httpsCallable } from 'firebase/functions';

class InvoicePDFService {
  constructor() {
    this.functions = getFunctions();
    // Set region to match your Firebase functions region
    this.functions.region = 'me-central1';
  }

  /**
   * Generate and download invoice PDF
   * @param {string} invoiceId - The invoice ID or invoice number
   * @param {string} companyId - The company ID
   * @param {string} language - Language for PDF ('en' or 'ar')
   * @returns {Promise<void>}
   */
  async generateInvoicePDF(invoiceId, companyId, language = 'en') {
    try {
      console.log('🔄 Generating invoice PDF...', { invoiceId, companyId, language });

      // Call the Firebase function directly via HTTP
      const projectId = this.functions.app.options.projectId || 'logistiat-saas';
      const functionUrl = `https://me-central1-${projectId}.cloudfunctions.net/generateInvoicePDF`;

      console.log('🔗 Calling function URL:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoiceId,
          companyId,
          language
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server response:', errorText);

        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }

        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        const responseText = await response.text();
        console.error('❌ Expected PDF but got:', contentType, responseText);
        throw new Error('Server did not return a PDF file');
      }

      // Get the PDF blob
      const pdfBlob = await response.blob();

      if (pdfBlob.size === 0) {
        throw new Error('Generated PDF is empty');
      }

      console.log('📄 PDF blob size:', pdfBlob.size);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}-${language}.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      console.log('✅ Invoice PDF generated and downloaded successfully');
      return true;
    } catch (error) {
      console.error('❌ Error generating invoice PDF:', error);
      throw new Error(`Failed to generate PDF: ${error.message}`);
    }
  }

  /**
   * Generate invoice PDF and return as blob (for preview)
   * @param {string} invoiceId - The invoice ID or invoice number
   * @param {string} companyId - The company ID
   * @param {string} language - Language for PDF ('en' or 'ar')
   * @returns {Promise<Blob>}
   */
  async generateInvoicePDFBlob(invoiceId, companyId, language = 'en') {
    try {
      console.log('🔄 Generating invoice PDF blob...', { invoiceId, companyId, language });

      const projectId = this.functions.app.options.projectId || 'logistiat-saas';
      const functionUrl = `https://me-central1-${projectId}.cloudfunctions.net/generateInvoicePDF`;

      console.log('🔗 Calling function URL:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoiceId,
          companyId,
          language
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server response:', errorText);

        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }

        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        const responseText = await response.text();
        console.error('❌ Expected PDF but got:', contentType, responseText);
        throw new Error('Server did not return a PDF file');
      }

      const pdfBlob = await response.blob();

      if (pdfBlob.size === 0) {
        throw new Error('Generated PDF is empty');
      }

      console.log('✅ Invoice PDF blob generated successfully, size:', pdfBlob.size);
      return pdfBlob;
    } catch (error) {
      console.error('❌ Error generating invoice PDF blob:', error);
      throw error;
    }
  }

  /**
   * Open invoice PDF in new tab
   * @param {string} invoiceId - The invoice ID or invoice number
   * @param {string} companyId - The company ID
   * @param {string} language - Language for PDF ('en' or 'ar')
   * @returns {Promise<void>}
   */
  async openInvoicePDF(invoiceId, companyId, language = 'en') {
    try {
      const pdfBlob = await this.generateInvoicePDFBlob(invoiceId, companyId, language);
      const url = window.URL.createObjectURL(pdfBlob);

      // Open in new tab
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Clean up URL after a delay to allow the browser to load it
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      console.log('✅ Invoice PDF opened in new tab');
    } catch (error) {
      console.error('❌ Error opening invoice PDF:', error);
      throw error;
    }
  }
}

export const invoicePDFService = new InvoicePDFService();
export default invoicePDFService;